import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  BulkDeleteUnmatchedOrderRequest,
  BulkDeleteUnmatchedOrderSuccessResponse,
  BulkRegisterOrderBusinessesFromUnmatchedRequest,
  BulkRegisterOrderBusinessesFromUnmatchedSuccessResponse,
  GetExistingOrdersSuccessResponse,
  GetUnmatchedOrderSuccessResponse,
  PutUnmatchedOrderRequest,
  RegisterUnmatchedOrderBusinessRequest,
  UnmatchedOrderApi,
} from '../axios/generated/unmatchedOrderManage';
import { IError } from '../interfaces/IError';
import { IUnmatchedOrderRepository } from '../interfaces/IUnmatchedOrderRepository';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';
import { IFilterInfoResponse } from '../interfaces/IFilterInfoRepository';

/**
 * 未照合・差異一覧のリポジトリ
 */
export class UnmatchedOrderRepository implements IUnmatchedOrderRepository {
  /**
   * 未照合・差異一覧情報を取得する
   * @param limit - 取得上限
   * @param offset - オフセット
   * @param filterItem - カラムベースのフィルターパラメータ
   */
  async get(
    limit: number,
    offset: number,
    filterItem: { [key: string]: string } = {}
  ): Promise<INetworkResult<GetUnmatchedOrderSuccessResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const unmatchedOrderApi = new UnmatchedOrderApi(undefined, undefined, axiosInstance);

      const apiResponse = await unmatchedOrderApi.getUnmatchedOrder(limit, offset, filterItem);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 突合先候補の既存発注を取得する
   */
  async getExistingOrders(): Promise<INetworkResult<GetExistingOrdersSuccessResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const unmatchedOrderApi = new UnmatchedOrderApi(undefined, undefined, axiosInstance);

      const apiResponse = await unmatchedOrderApi.getExistingOrders();
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 未照合・差異一覧情報を更新する
   * @param request - 更新リクエスト
   */
  async put(request: PutUnmatchedOrderRequest): Promise<INetworkResult<void, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const unmatchedOrderApi = new UnmatchedOrderApi(undefined, undefined, axiosInstance);

      const apiResponse = await unmatchedOrderApi.putUnmatchedOrder(request);

      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 未照合の発注取引を登録する
   * @param request - 登録情報
   * @returns - なし
   */
  async register(request: RegisterUnmatchedOrderBusinessRequest): Promise<INetworkResult<void, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const unmatchedOrderApi = new UnmatchedOrderApi(undefined, undefined, axiosInstance);
      const apiResponse = await unmatchedOrderApi.registerUnmatchedOrderBusiness(request);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: undefined } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 未照合の発注取引を一括で登録する
   * @param request - 登録情報
   * @returns - 登録結果
   */
  async bulkRegister(
    request: BulkRegisterOrderBusinessesFromUnmatchedRequest
  ): Promise<INetworkResult<BulkRegisterOrderBusinessesFromUnmatchedSuccessResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const unmatchedOrderApi = new UnmatchedOrderApi(undefined, undefined, axiosInstance);
      const apiResponse = await unmatchedOrderApi.bulkRegisterOrderBusinessesFromUnmatched(request);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 未照合・差異一覧情報を一括削除する
   * @param request - 一括削除リクエスト
   */
  async bulkDelete(request: BulkDeleteUnmatchedOrderRequest): Promise<INetworkResult<BulkDeleteUnmatchedOrderSuccessResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const unmatchedOrderApi = new UnmatchedOrderApi(undefined, undefined, axiosInstance);

      const apiResponse = await unmatchedOrderApi.deleteUnmatchedOrderBulk(request);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 未照合差異のフィルタ情報を取得する
   * @param column - 取得するカラム
   * @param keyword - キーワード
   */
  async getFilter(column: string, keyword?: string): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const unmatchedOrderApi = new UnmatchedOrderApi(undefined, undefined, axiosInstance);

      const apiResponse = await unmatchedOrderApi.getUnmatchedOrderFilter(column, keyword);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result;
          return {
            status: {
              type: 'success',
              data: {
                data: result,
                pagingOptions: {
                  pageSize: result.length,
                  offset: result.length,
                  returnCount: result.length,
                },
              },
            },
          };
        default:
          return {
            status: {
              type: 'error',
              error: {
                statusCode: -1,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
